import { useState } from "react";

import { useTranslation } from "react-i18next";

import useAxiosPrivate from "../../../../api/hooks/useAxiosPrivate";
import { useAuth } from "../../../../context/Auth/AuthProvider";
import { ALERT_STATUS } from "../../../../context/alert/types";
import { GeneralDialogProps, useMediaQueries } from "../../../../shared";
import DialogActions from "../../../../shared/components/buttons/DialogActions";
import useResponse from "../../../../shared/hooks/useResponse";
import Dialog from "../../../Dialog/Dialog";
import { DialogWidth } from "../../../Dialog/types/enums";
import { useScheduleFormInputs } from "../../hooks/useScheduleFormInputs";
import { ResourceParams } from "../../types";
import ScheduleFormContent from "../ScheduleFormContent/ScheduleFormContent";

interface CreateScheduleDialogProps extends GeneralDialogProps {
  resourceParams: ResourceParams;
  getSchedulesData: () => Promise<void>;
}

const CreateScheduleDialog = ({
  resourceParams,
  isOpen,
  onClose,
  getSchedulesData
}: CreateScheduleDialogProps) => {
  const { user } = useAuth();
  const { toMd } = useMediaQueries();
  const { t } = useTranslation();

  const { postData } = useAxiosPrivate();
  const { handleCommonError, handleResponse } = useResponse();

  const { resourceName, actionCreate, apiResource } = resourceParams;

  const {
    schedulePayload,
    repeatPeriod,
    inputError,
    readyToSubmit,
    inputValues,
    startDateTime,
    resetValueStates,
    validateScheduleName,
    handleFormChange,
    handleTimeChange
  } = useScheduleFormInputs({ resourceName });

  const [isLoading, setIsLoading] = useState(false);

  const { fileTypes } = schedulePayload;

  const handleCloseDialog = () => onClose(actionCreate);

  const handleCreate = async () => {
    try {
      setIsLoading(true);
      await postData(apiResource, schedulePayload);

      handleResponse(ALERT_STATUS.Success, t("Alert##create schedule success"));
      handleCloseDialog();
      getSchedulesData();
    } catch (error) {
      handleCommonError(error, t);
    }
    setIsLoading(false);
  };

  return (
    <Dialog
      data-testid="create-schedule-dialog"
      title={t(`Dialog##create schedule`)}
      description={t(`Dialog##create schedule description`)}
      open={isOpen}
      isTitleSeparator
      isActionsSeparator
      extendToMaxWidth={toMd}
      width={toMd ? undefined : DialogWidth.MinWidth}
      close={handleCloseDialog}
      submit={handleCreate}
      slotProps={{ transition: { onExited: () => resetValueStates() } }}
      actions={
        <DialogActions
          onClose={handleCloseDialog}
          isLoading={isLoading}
          isreadyToConfirm={readyToSubmit}
          confirmationButtonText={t("Button##create")}
          confirmationButtonTestId="create-schedule-confirmation-button"
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="create-schedule-cancel-button"
        />
      }
    >
      <ScheduleFormContent
        inputValues={inputValues}
        inputError={inputError}
        repeatValue={repeatPeriod}
        startDateTime={startDateTime}
        selectedDataTypes={fileTypes}
        handleFormChange={handleFormChange}
        handleTimeChange={handleTimeChange}
        validateScheduleName={validateScheduleName}
        createSchedule
        isLoading={isLoading}
        user={user}
      />
    </Dialog>
  );
};

export default CreateScheduleDialog;
